// src/components/dashboard/PlanCard.tsx
import React from 'react';
import { Link } from 'react-router-dom';
import {
  EntitlementGrantStatus,
  EntitlementType,
} from '../../models/entitlement/EntitlementSummary';
import type { Membership } from '../../models/Membership';

interface PlanCardProps {
  membership: Membership;
  /** The credits balance (useAuth); null while it loads. */
  balance: number | null;
}

/** Format a calendar-date ISO string (e.g. '2026-09-18T00:00:00' or '2026-09-18')
 *  as a locale date string WITHOUT any UTC coercion, so the displayed date always
 *  matches the calendar date the backend intended regardless of the user's timezone. */
function formatExpiryDate(iso: string): string {
  const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return iso;
  const [, y, m, d] = match.map(Number);
  return new Date(y, m - 1, d).toLocaleDateString();
}

/**
 * The member's card on the home (board HomeV2): the tier, today's included hours and how long the
 * pass runs, the credits balance, and the ONE Book a session button. Without an active entitlement
 * the tier and the button stay; the hours and the date go.
 */
const PlanCard: React.FC<PlanCardProps> = ({ membership, balance }) => {
  const ent = membership.entitlement;
  const active = ent?.status === EntitlementGrantStatus.Active ? ent : null;

  // No entitlement at all: a membership. Otherwise the type decides; an absent type is a Pass (older responses).
  const isMembership = !ent || ent.type === EntitlementType.Membership;
  const badgeLabel = isMembership ? 'Membership' : 'Season Pass';

  const hours = active ? active.dailyMinutesAllowance / 60 : 0;
  const hoursLabel = `${Number.isInteger(hours) ? hours : hours.toFixed(1)} hrs included`;
  const expiryValue = active?.expiresAt ? formatExpiryDate(active.expiresAt) : 'Activates on first use';

  return (
    <div className="entitlement-card">
      <div className="ent-stripe" aria-hidden="true" />
      <div className="ent-body">
        <span className="ent-badge">
          {!isMembership && <span aria-hidden="true">★ </span>}
          {badgeLabel}
        </span>
        <h2 className="ent-name">{membership.name}</h2>
        {active && (
          <div className="ent-stats">
            <div className="ent-stat">
              <div className="ent-stat-key">Today</div>
              <div className="ent-stat-value">{hoursLabel}</div>
            </div>
            <div className="ent-stat">
              <div className="ent-stat-key">Valid until</div>
              <div className="ent-stat-value ent-stat-value--plain">{expiryValue}</div>
            </div>
          </div>
        )}
        <div className="ent-credits">
          <div>
            <div className="ent-stat-key">Credits</div>
            <div className="ent-stat-value ent-stat-value--plain">{balance ?? '—'}</div>
          </div>
          <Link to="/checkout" className="ent-link">Buy credits ›</Link>
        </div>
        <Link to="/book" className="ent-cta">Book a session</Link>
      </div>
    </div>
  );
};

export default PlanCard;
